/**
 * Studio theme state — light / dark / system.
 *
 * First paint is handled by the inline bootstrap in index.html, which
 * reads the saved preference before React mounts. This hook reads +
 * persists that preference and flips `data-theme` on <html> when the
 * merchant toggles.
 *
 * "system" = follow OS prefers-color-scheme live (updates when the OS
 * scheme flips). "light" / "dark" = override regardless of OS.
 */
import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "cp:studio:theme";

function readStored(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "light" || v === "dark" || v === "system") return v;
  } catch {
    // private-browsing Safari throws on localStorage access — treat as
    // "no preference" and defer to OS.
  }
  // No explicit preference — read whatever the bootstrap script stamped.
  const stamped = document.documentElement.getAttribute("data-theme");
  return stamped === "dark" ? "dark" : stamped === "light" ? "light" : "system";
}

function systemResolved(): ResolvedTheme {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolve(mode: ThemeMode): ResolvedTheme {
  return mode === "system" ? systemResolved() : mode;
}

function applyToDocument(resolved: ResolvedTheme): void {
  document.documentElement.setAttribute("data-theme", resolved);
}

export function useTheme(): {
  mode: ThemeMode;
  resolved: ResolvedTheme;
  setMode: (m: ThemeMode) => void;
  cycle: () => void;
} {
  const [mode, setModeState] = useState<ThemeMode>(() => readStored());
  const [resolved, setResolved] = useState<ResolvedTheme>(() => resolve(readStored()));

  const setMode = useCallback((m: ThemeMode) => {
    setModeState(m);
    try {
      localStorage.setItem(STORAGE_KEY, m);
    } catch {
      // ignore — private-browsing Safari will just not persist.
    }
    const r = resolve(m);
    setResolved(r);
    applyToDocument(r);
  }, []);

  const cycle = useCallback(() => {
    setMode(mode === "light" ? "dark" : mode === "dark" ? "system" : "light");
  }, [mode, setMode]);

  // When the merchant has mode="system", follow OS changes live.
  useEffect(() => {
    if (mode !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const r = systemResolved();
      setResolved(r);
      applyToDocument(r);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [mode]);

  return { mode, resolved, setMode, cycle };
}
